import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Search, Filter, Loader2, CheckCircle, XCircle, AlertTriangle, Clock, ExternalLink, Inbox, Settings } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const STATUS_ICONS = {
  sent: <CheckCircle size={14} className="text-green-600" />,
  failed: <XCircle size={14} className="text-destructive" />,
  skipped: <AlertTriangle size={14} className="text-amber-500" />,
  pending: <Clock size={14} className="text-muted-foreground" />,
  duplicate: <Mail size={14} className="text-muted-foreground" />,
};

function StatCard({ label, value, color = "text-primary" }) {
  return (
    <div className="border border-border p-4 bg-card">
      <p className={`text-2xl font-display ${color}`}>{value}</p>
      <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

const WEBMAIL_KEY = "aethel_webmail_url";

const EMAIL_ACCOUNTS = [
  { dept: "Sales", email: "sales@aetheldecor.com", desc: "Sales enquiries, quotations", color: "#1A3626" },
  { dept: "Support", email: "support@aetheldecor.com", desc: "Customer support, complaints", color: "#2a5a3f" },
  { dept: "Info", email: "info@aetheldecor.com", desc: "General enquiries", color: "#3d7a4a" },
  { dept: "Orders", email: "orders@aetheldecor.com", desc: "Order confirmations, status", color: "#D4AF37" },
  { dept: "Vendors", email: "vendors@aetheldecor.com", desc: "Vendor/supplier comms", color: "#8B7524" },
  { dept: "Accounts", email: "accounts@aetheldecor.com", desc: "Payments, invoices, refunds", color: "#5D4037" },
  { dept: "Marketing", email: "marketing@aetheldecor.com", desc: "Campaigns, promotions", color: "#6D4C41" },
  { dept: "No-Reply", email: "no-reply@aetheldecor.com", desc: "Automated notifications", color: "#9e9e9e" },
];

function EmailAccountsPanel() {
  const [webmailUrl, setWebmailUrl] = useState(() => localStorage.getItem(WEBMAIL_KEY) || "");
  const [editing, setEditing] = useState(false);
  const [tempUrl, setTempUrl] = useState(webmailUrl);

  const saveUrl = () => {
    const url = tempUrl.trim();
    setWebmailUrl(url);
    localStorage.setItem(WEBMAIL_KEY, url);
    setEditing(false);
    if (url) toast.success("Webmail URL saved");
  };

  const openMailbox = (email) => {
    if (webmailUrl) {
      // If webmail URL is configured, open it (e.g., Gmail, Zoho)
      window.open(webmailUrl, "_blank");
    } else {
      // Fallback to mailto:
      window.location.href = `mailto:${email}`;
    }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid="email-accounts-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-2">
          <Inbox size={14} className="text-primary" /> Email Accounts
        </p>
        <button
          onClick={() => { setEditing(!editing); setTempUrl(webmailUrl); }}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
          data-testid="email-accounts-settings"
        >
          <Settings size={12} /> {editing ? "Cancel" : "Configure Webmail"}
        </button>
      </div>

      {/* Webmail URL config */}
      {editing && (
        <div className="mb-4 p-3 bg-muted/50 border border-border rounded-lg">
          <p className="text-xs text-muted-foreground mb-2">
            Set your webmail login URL. All mailbox icons will open this URL.
            <br />
            <span className="text-[10px]">E.g., <code className="bg-muted px-1">https://mail.google.com</code> for Google Workspace, <code className="bg-muted px-1">https://mail.zoho.com</code> for Zoho</span>
          </p>
          <div className="flex gap-2">
            <input
              type="url"
              value={tempUrl}
              onChange={(e) => setTempUrl(e.target.value)}
              placeholder="https://mail.google.com"
              className="flex-1 bg-white px-3 py-2 text-sm outline-none border border-border focus:border-primary rounded"
              data-testid="webmail-url-input"
            />
            <button
              onClick={saveUrl}
              className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded"
              data-testid="webmail-url-save"
            >
              Save
            </button>
          </div>
          {!webmailUrl && (
            <p className="text-[10px] text-amber-600 mt-2">
              No webmail configured yet. Clicking a mailbox will open your default email client (mailto:).
              To use Gmail with your domain, set up <a href="https://workspace.google.com" target="_blank" rel="noreferrer" className="underline">Google Workspace</a>.
            </p>
          )}
        </div>
      )}

      {/* Accounts grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {EMAIL_ACCOUNTS.map((acct) => (
          <button
            key={acct.email}
            onClick={() => openMailbox(acct.email)}
            className="flex items-center gap-2.5 px-3 py-3 border border-border rounded-lg hover:border-primary/30 hover:bg-secondary/30 transition-all text-left group"
            title={`Open ${acct.email}`}
            data-testid={`email-account-${acct.dept.toLowerCase()}`}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: acct.color + "18" }}
            >
              <Mail size={14} style={{ color: acct.color }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground leading-tight truncate">{acct.dept}</p>
              <p className="text-[10px] text-muted-foreground leading-tight truncate">{acct.email.split("@")[0]}@</p>
            </div>
            <ExternalLink size={10} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </button>
        ))}
      </div>

      {webmailUrl && (
        <p className="text-[10px] text-muted-foreground mt-3 flex items-center gap-1">
          <ExternalLink size={9} /> Webmail: <a href={webmailUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">{webmailUrl}</a>
        </p>
      )}
    </div>
  );
}

export default function AdminEmailTab() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", email_type: "all", department: "all", to: "" });
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        api.get("/admin/emails/stats"),
        api.get("/admin/emails/logs", { params: { ...filters, limit: 50 } }),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs);
      setTotal(logsRes.data.total);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const applyFilters = () => loadData();

  const sendTest = async () => {
    if (!testEmail.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/emails/test", { to: testEmail });
      toast.success(`Test email ${data.result?.status === "sent" ? "sent" : "logged (provider not configured)"}`);
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Emails" value={stats.total} />
          <StatCard label="Sent" value={stats.sent} color="text-green-600" />
          <StatCard label="Failed" value={stats.failed} color="text-destructive" />
          <StatCard label="Skipped" value={stats.skipped} color="text-amber-500" />
          <StatCard label="Pending" value={stats.pending} />
          <StatCard label="Duplicates Prevented" value={stats.duplicate} />
        </div>
      )}

      {/* Email Accounts */}
      <EmailAccountsPanel />

      {/* Test Email */}
      <div className="border border-border p-5 bg-card" data-testid="email-test-section">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3 flex items-center gap-2">
          <Send size={14} className="text-primary" /> Send Test Email
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email address..."
            className="flex-1 bg-muted px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
            data-testid="test-email-input"
          />
          <button
            onClick={sendTest}
            disabled={sending || !testEmail.trim()}
            className="bg-primary text-primary-foreground px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="test-email-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-border p-4 bg-card">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3 flex items-center gap-2">
          <Filter size={14} /> Filters
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="bg-muted px-3 py-2 text-sm outline-none"
            data-testid="email-filter-status"
          >
            <option value="all">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={filters.department}
            onChange={(e) => setFilters(f => ({ ...f, department: e.target.value }))}
            className="bg-muted px-3 py-2 text-sm outline-none"
            data-testid="email-filter-department"
          >
            <option value="all">All Departments</option>
            {["orders","support","sales","info","vendors","accounts","marketing","no-reply"].map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <input
            type="text"
            value={filters.to}
            onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
            placeholder="Search by email..."
            className="bg-muted px-3 py-2 text-sm outline-none"
            data-testid="email-filter-to"
          />
          <button
            onClick={applyFilters}
            className="bg-primary text-primary-foreground px-4 py-2 text-sm flex items-center gap-2 justify-center"
            data-testid="email-filter-apply"
          >
            <Search size={14} /> Apply
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="border border-border bg-card overflow-x-auto" data-testid="email-logs-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Type</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">To</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Subject</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Dept</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                <Loader2 size={20} className="animate-spin mx-auto" />
              </td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No email logs found.</td></tr>
            ) : logs.map((log) => (
              <tr key={log.id} className="border-b border-border/50 hover:bg-secondary/30">
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1.5">
                    {STATUS_ICONS[log.status] || STATUS_ICONS.pending}
                    <span className="capitalize">{log.status}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{log.email_type?.replace(/_/g, " ")}</td>
                <td className="px-4 py-3">{log.to}</td>
                <td className="px-4 py-3 max-w-[200px] truncate" title={log.subject}>{log.subject}</td>
                <td className="px-4 py-3 text-muted-foreground">{log.department}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                  {new Date(log.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > 50 && (
          <p className="px-4 py-3 text-xs text-muted-foreground border-t border-border">Showing 50 of {total} logs</p>
        )}
      </div>
    </div>
  );
}
